import { CONTENT_ANALYTICS } from 'constants/types';
import { convertSnakeCaseKeysToCamelCase } from 'utils';
import { IContactAnalyticsItem } from '../interfaces';

export interface Contact {
  contactEmail: string;
  contactId: number;
  clickCount: number;
  contactColor: string;
  contactImage: string;
  contactName: string;
}
export interface ClickedBy {
  totalCount: number;
  contacts: Contact[];
  thumbnailContacts: Contact[];
}

export interface Email {
  emailId: string;
  emailName: string;
}

export interface IArticle {
  articleId: number;
  articleTitle: string;
  articleImage: string;
  articleLink: string;
  clickedBy: ClickedBy;
  articleScore: number;
  emailName: string;
  emailDate: string;
}

export interface IAnalyticsContent {
  data: IArticle[];
  searchedData: IArticle[];
  totalCount: number;
  loading: boolean;
  loaded: boolean;
  filterReset: boolean;
  searchReset: boolean;
  emailSearchValue: string;
  sortDirection: string;
  contentContacts: IContactAnalyticsItem[];
  contentContactsLoading: boolean;
  totalContentContacts: number;
  totalArticleScore: number;
  articleTitle: string;
  articleUrl: string;
}

const initialAnalyticsContent = {
  data: [],
  searchedData: [],
  totalCount: 0,
  loading: false,
  loaded: false,
  filterReset: false,
  searchReset: false,
  emailSearchValue: '',
  sortDirection: 'NOSORT',
  contentContacts: Array<IContactAnalyticsItem>(),
  contentContactsLoading: false,
  totalContentContacts: 0,
};

export const analyticsContentReducer = (state: IAnalyticsContent = initialAnalyticsContent, action: any) => {
  const newState = { ...state };
  switch (action.type) {
    case CONTENT_ANALYTICS.FILTER_RESET:
      newState.filterReset = action.payload;
      return newState;
    case CONTENT_ANALYTICS.SEARCH_RESET:
      newState.searchReset = action.payload;
      return newState;
    case CONTENT_ANALYTICS.SET_SEARCH:
      let searchValue = action.payload.searchValue;
      let articleIndex = action.payload.articleIndex;
      let articleId = action.payload.articleId;
      if (searchValue.length > 0) {
        newState.emailSearchValue = searchValue;
        let articleItem: IArticle = state.data.filter((item: any, index: number) => item.articleId === articleId && index === articleIndex)[0];
        let srchEmails = articleItem.clickedBy.contacts.filter((item: any) => {
          const toInclude =
            item.contactEmail?.toLowerCase().includes(searchValue.toLowerCase()) ||
            item.contactName?.toLowerCase().includes(searchValue.toLowerCase());
          return toInclude;
        });

        if (newState.sortDirection === 'ASC') {
          srchEmails = srchEmails.sort((a, b) => {
            return a.clickCount - b.clickCount;
          });
        } else if (newState.sortDirection === 'DESC') {
          srchEmails = srchEmails.sort((a, b) => {
            return b.clickCount - a.clickCount;
          });
        }
        newState.searchedData = [...srchEmails];
      } else {
        newState.searchedData = [];
        newState.emailSearchValue = '';
      }
      return newState;
    case CONTENT_ANALYTICS.CLICKED_CONTACTS_SORT:
      let aId = action.payload.articleId;
      let articleItem: IArticle = state.data[action.payload.index];
      let sortedContacts: Contact[] = [];
      let sortedSearchContacts = [];

      if (articleItem.articleId === aId) {
        if (action.payload.sort === 'ASC') {
          sortedContacts = articleItem.clickedBy.contacts.sort((a, b) => {
            return a.clickCount - b.clickCount;
          });
          articleItem.clickedBy.contacts = [...sortedContacts];

          sortedSearchContacts = newState.searchedData.sort((a, b) => {
            return a.clickCount - b.clickCount;
          });
          newState.searchedData = [...sortedSearchContacts];
        } else if (action.payload.sort === 'DESC' || action.payload.sort === 'NOSORT') {
          sortedContacts = articleItem.clickedBy.contacts.sort((a, b) => {
            return b.clickCount - a.clickCount;
          });
          articleItem.clickedBy.contacts = [...sortedContacts];

          sortedSearchContacts = newState.searchedData.sort((a, b) => {
            return b.clickCount - a.clickCount;
          });
          newState.searchedData = [...sortedSearchContacts];
        }
      }
      newState.sortDirection = action.payload.sort;

      return newState;
    case CONTENT_ANALYTICS.LOAD:
      newState.loading = true;
      newState.loaded = false;
      newState.data = [];
      newState.totalCount = 0;
      break;
    case CONTENT_ANALYTICS.LOAD_SUCCESS:
      newState.loading = false;
      newState.loaded = true;
      let articleData = [...action.payload?.data?.articles?.map((content: any) => convertSnakeCaseKeysToCamelCase(content))];
      articleData = articleData.map((article: any) => {
        article.clickedBy.thumbnailContacts = article.clickedBy.contacts.slice(0, 5);
        return article;
      });
      newState.data = [...articleData];
      newState.totalCount = action.payload.data.total_articles_count;
      break;
    case CONTENT_ANALYTICS.LOAD_MORE:
      newState.loading = true;
      newState.loaded = false;
      break;
    case CONTENT_ANALYTICS.LOAD_MORE_SUCCESS:
      newState.loading = false;
      newState.loaded = true;
      let data = [...action.payload?.data?.articles?.map((content: any) => convertSnakeCaseKeysToCamelCase(content))];
      data = data.map((article: any) => {
        article.clickedBy.thumbnailContacts = article.clickedBy.contacts.slice(0, 5);
        return article;
      });
      newState.data = [...newState.data, ...data];

      newState.totalCount = action.payload.data.total_articles_count;
      break;
    case CONTENT_ANALYTICS.LOAD_FAIL:
      newState.loading = false;
      newState.loaded = false;
      newState.data = [];
      newState.totalCount = 0;
      break;
    case CONTENT_ANALYTICS.RESET:
      newState.loaded = false;
      newState.loading = false;
      newState.data = [];
      newState.totalCount = 0;
      newState.contentContacts = [];
      break;
    case CONTENT_ANALYTICS.LOAD_CONTACTS:
      newState.contentContactsLoading = true;
      return newState;
    case CONTENT_ANALYTICS.LOAD_CONTACTS_SUCCESS:
      newState.contentContactsLoading = false;
      if (action.payload.offset === 0) {
        newState.totalContentContacts = action.payload.total_delivered ? action.payload.total_delivered : 0;
        newState.contentContacts = action.payload.delivered;
        newState.totalArticleScore = action.payload.total_article_score;
        if (!action.payload.is_Search && !action.payload.is_Sort) {
          newState.articleUrl = action.payload.article_url;
          newState.articleTitle = action.payload.article_title;
        }
      } else {
        newState.contentContacts = [...newState.contentContacts, ...action.payload.delivered];
      }
      return newState;
    case CONTENT_ANALYTICS.RESET_CONTATCS:
      newState.contentContacts = [];
      newState.contentContactsLoading = false;
      newState.totalContentContacts = 0;
      newState.totalArticleScore = 0;
      return newState;
    case CONTENT_ANALYTICS.RESET_CONTACTS_ONLY:
      newState.contentContacts = [];
      newState.contentContactsLoading = false;
      return newState;
    case CONTENT_ANALYTICS.RESET_ARTILCE_DATA:
      newState.articleTitle = '';
      newState.articleUrl = '';
      return newState;
    case CONTENT_ANALYTICS.CONTACTS_FAIL:
      newState.contentContactsLoading = false;
      return newState;

    default:
      break;
  }
  return newState;
};
